import type { TopLevelSpec } from "vega-lite";
import { isAllowedGeoBasemapUrl } from "@erp/shared-utils";

/**
 * Safely parse + vet an untrusted Vega-Lite spec (e.g. one generated by an AI
 * agent) before rendering it in the browser. Enforces inline-data-only: a spec
 * is rendered by Vega in the user's browser, so a `data.url` or image mark
 * would let it fetch arbitrary resources. The one exception is geographic base
 * maps served by our own backend (`isAllowedGeoBasemapUrl`): their topology is
 * too large to inline, so a `data.url` pointing at our same-origin geo route is
 * allowed. Mirrors the backend guard in the chat-agent's `render-chart` tool —
 * keep the two in sync.
 */

const RENDERABLE_ROOT_KEYS = [
  "mark",
  "layer",
  "facet",
  "concat",
  "hconcat",
  "vconcat",
  "repeat",
  "spec",
] as const;

export type ParseChartSpecResult =
  | { ok: true; spec: TopLevelSpec }
  | { ok: false; error: string };

function dataLoadsRemote(data: unknown): boolean {
  if (Array.isArray(data)) return data.some(dataLoadsRemote);
  if (!data || typeof data !== "object") return false;
  const url = (data as Record<string, unknown>).url;
  if (url === undefined) return false;
  // A `data.url` is a forbidden remote load unless it points at an allowlisted,
  // same-origin geo base map served by our backend.
  return !isAllowedGeoBasemapUrl(url);
}

/** True when any node loads an allowlisted geo base map via `data.url`. */
function referencesGeoBasemap(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(referencesGeoBasemap);
  if (!node || typeof node !== "object") return false;
  const obj = node as Record<string, unknown>;
  const data = obj.data;
  if (
    data &&
    typeof data === "object" &&
    isAllowedGeoBasemapUrl((data as Record<string, unknown>).url)
  ) {
    return true;
  }
  return Object.values(obj).some(referencesGeoBasemap);
}

function specHasRemoteLoad(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(specHasRemoteLoad);
  if (!node || typeof node !== "object") return false;
  const obj = node as Record<string, unknown>;

  if ("data" in obj && dataLoadsRemote(obj.data)) return true;
  if (obj.mark === "image") return true;
  if (
    typeof obj.mark === "object" &&
    obj.mark !== null &&
    (obj.mark as Record<string, unknown>).type === "image"
  ) {
    return true;
  }

  return Object.values(obj).some(specHasRemoteLoad);
}

function hasInlineData(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(hasInlineData);
  if (!node || typeof node !== "object") return false;
  const obj = node as Record<string, unknown>;
  const data = obj.data;
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as Record<string, unknown>).values)
  ) {
    return true;
  }
  return Object.values(obj).some(hasInlineData);
}

/**
 * Parses a Vega-Lite spec from a JSON string (or accepts an already-parsed
 * object) and validates it is safe + renderable. Returns the typed spec or a
 * human-readable error.
 */
export function parseChartSpec(input: unknown): ParseChartSpecResult {
  let parsed: unknown = input;
  if (typeof input === "string") {
    try {
      parsed = JSON.parse(input);
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Invalid JSON.",
      };
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "Spec must be a JSON object." };
  }

  const spec = parsed as Record<string, unknown>;
  if (!RENDERABLE_ROOT_KEYS.some((key) => key in spec)) {
    return { ok: false, error: "Spec has no mark or composition to render." };
  }
  if (!hasInlineData(spec) && !referencesGeoBasemap(spec)) {
    return {
      ok: false,
      error: "Spec has no inline data (data.values) or geo base map.",
    };
  }
  if (specHasRemoteLoad(spec)) {
    return { ok: false, error: "Remote data loads are not allowed." };
  }

  // Validated above: an object with a renderable root and either inline data or
  // an allowlisted geo base map. Vega surfaces any remaining encoding errors at
  // render time via VegaChart.
  return { ok: true, spec: spec as unknown as TopLevelSpec };
}
